import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Reseller dashboard — overview + setup checklist.
 *
 * Phase 2 = checklist of "set up brand", "connect email", and a roadmap of
 * what's coming. Phase 7+ replaces the placeholder cards with real KPIs
 * (bookings count, revenue YTD, pending payouts, etc.).
 */
export default function ResellerDashboard() {
  const { t } = useLanguage()
  const [me, setMe] = useState(null)
  const [brand, setBrand] = useState(null)
  const [emailIntegrations, setEmailIntegrations] = useState([])
  const [kpis, setKpis] = useState(null)
  const [health, setHealth] = useState(null)
  const [coursePay, setCoursePay] = useState({ unpaid: 0, overdue: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      tournamentApi.get('/portal/reseller/me').catch(() => ({ data: null })),
      tournamentApi.get('/portal/reseller/brand').catch(() => ({ data: { brand: null } })),
      tournamentApi.get('/portal/reseller/email-integrations').catch(() => ({ data: { integrations: [] } })),
      tournamentApi.get('/portal/reseller/dashboard-kpis').catch(() => ({ data: null })),
      tournamentApi.get('/portal/reseller/booking-health').catch(() => ({ data: null })),
      tournamentApi.get('/portal/reseller/course-payments', { params: { status: 'unpaid', pageSize: 1 } }).catch(() => ({ data: { counts: { unpaid: 0, overdue: 0 } } })),
    ]).then(([m, b, e, k, h, cp]) => {
      setMe(m.data)
      setBrand(b.data?.brand || null)
      setEmailIntegrations(e.data?.integrations || [])
      setKpis(k.data || null)
      setHealth(h.data || null)
      setCoursePay(cp.data?.counts || { unpaid: 0, overdue: 0 })
      setLoading(false)
    })
  }, [])

  async function reloadHealth() {
    const { data } = await tournamentApi.get('/portal/reseller/booking-health').catch(() => ({ data: null }))
    setHealth(data || null)
  }
  async function recheckNow() {
    await tournamentApi.post('/portal/reseller/booking-health/recheck', {}).catch(() => null)
    // The cron run is async — give it ~3s to write rows then refresh.
    setTimeout(reloadHealth, 3000)
  }

  if (loading) return <ResellerShell><div style={{ color: '#64748b' }}>{t('sp_loading_short', 'Loading…')}</div></ResellerShell>

  const account = me?.account
  const hasBrand = !!brand && (brand.LogoUrl || brand.PrimaryColor || brand.LegalName)
  const hasEmail = emailIntegrations.some(i => i.IsActive)

  return (
    <ResellerShell title={t('sp_dash_welcome_back', 'Welcome back, {name}', { name: account?.Name || t('sp_dash_reseller_fallback', 'Reseller') })}>
      <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.6, maxWidth: 720, marginBottom: 24 }}>
        {t('sp_dash_intro_blurb', 'Your reseller portal is being built out in phases. Complete the setup steps below as features land. Right now you can configure your brand profile and email integration so customer-facing comms are ready when bookings start flowing.')}
      </p>

      <BookingHealthCard health={health} onResolve={reloadHealth} onRecheck={recheckNow} />

      {/* Setup checklist hidden for now — most items are stale phase
          markers and the real activation gating now lives on the
          Packages tab via ActivationChecklist. Re-enable when we want
          a top-level onboarding nudge again. */}
      {false && (
        <>
          <h2 style={h2}>{t('sp_dash_setup_checklist', 'Setup checklist')}</h2>
          <ChecklistRow done title={t('sp_dash_account_created', 'Account created')} detail={t('sp_dash_welcome_aboard', 'Welcome aboard.')} />
          <ChecklistRow done={hasBrand}  title={t('sp_brand_profile_title', 'Brand profile')} detail={hasBrand ? t('sp_dash_brand_saved', 'Logo + brand info saved.') : t('sp_dash_brand_detail', 'Add your logo, colors, and company info — used on every customer email and embed.')} cta={!hasBrand && { to: '/portal/reseller/brand', label: t('sp_dash_set_up_brand', 'Set up brand') }} />
          <ChecklistRow done={hasEmail}  title={t('sp_email_integration_title', 'Email integration')} detail={hasEmail ? t('sp_dash_email_active', 'Active: {provider}', { provider: emailIntegrations.find(i => i.IsActive)?.ProviderType }) : t('sp_dash_email_detail', 'Connect SendGrid or AWS SES so customer emails send from your domain.')} cta={!hasEmail && { to: '/portal/reseller/email-integration', label: t('sp_dash_connect_email', 'Connect email') }} />
          <ChecklistRow done={false} title={t('sp_dash_connect_courses', 'Connect courses')} detail={t('sp_dash_phase3_soon', 'Phase 3 — coming soon.')} soon />
          <ChecklistRow done={false} title={t('sp_payment_setup_title', 'Payment setup')} detail={t('sp_dash_phase4_soon', 'Phase 4 — coming soon.')} soon />
          <ChecklistRow done={false} title={t('sp_dash_build_groupings', 'Build groupings + packages')} detail={t('sp_dash_phase56_soon', 'Phases 5-6 — coming soon.')} soon />
        </>
      )}

      {kpis && (
        <>
          <h2 style={h2}>{t('sp_dash_at_a_glance', 'At a glance')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
            <Tile label={t('sp_dash_bookings_this_week', 'Bookings this week')}  value={kpis.bookingsThisWeek || 0} href="/portal/reseller/bookings" />
            <Tile
              label={t('sp_dash_trip_value_this_month', 'Trip value this month')}
              value={'$' + Number(kpis.tripValueThisMonth ?? kpis.revenueThisMonth ?? 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              sub={t('sp_dash_gross_customer_payments', 'gross customer payments')}
              href="/portal/reseller/bookings"
            />
            <Tile
              label={t('sp_dash_net_revenue_this_month', 'Net revenue this month')}
              value={'$' + Number(kpis.netRevenueThisMonth || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              sub={t('sp_dash_after_vendor_costs', 'after vendor costs + fees')}
              href="/portal/reseller/bookings"
            />
            <Tile label={t('sp_dash_upcoming_trips', 'Upcoming trips')}      value={kpis.upcomingTrips || 0} sub={t('sp_dash_next_30_days', 'next 30 days')} href="/portal/reseller/bookings" />
            <Tile
              label={t('sp_dash_customer_action_items', 'Customer-action items')}
              value={kpis.pendingCustomerActions || 0}
              urgent={kpis.pendingCustomerActions > 0}
              sub={kpis.pendingCustomerActions > 0 ? t('sp_dash_customers_waiting', 'Customers waiting on input') : t('sp_dash_all_clear', 'All clear')}
              href={kpis.pendingQuoteActions > 0 ? '/portal/reseller/custom-trips?action=requests' : '/portal/reseller/bookings?status=pending_customer_action'}
            />
            {kpis.refundsAwaitingApproval > 0 && (
              <Tile
                label={t('sp_dash_refunds_awaiting_approval', 'Refunds awaiting your approval')}
                value={kpis.refundsAwaitingApproval}
                urgent={true}
                sub={t('sp_dash_refunds_approval_sub', 'Approve or deny — click to review')}
                href={(kpis.refundsAwaitingApprovalList && kpis.refundsAwaitingApprovalList[0] && kpis.refundsAwaitingApprovalList[0].href) || '/portal/reseller/bookings'}
              />
            )}
            {kpis.refundsInProgress > 0 && (
              <Tile
                label={t('sp_dash_refunds_in_progress', 'Refunds in progress')}
                value={kpis.refundsInProgress}
                sub={t('sp_dash_refunds_processing', 'Being processed — click to view')}
                href={(kpis.refundsInProgressList && kpis.refundsInProgressList[0] && kpis.refundsInProgressList[0].href) || '/portal/reseller/bookings'}
              />
            )}
            {coursePay.unpaid > 0 && (
              <Tile
                label={t('sp_cp_dash_tile', 'Tee times to pay')}
                value={coursePay.unpaid}
                urgent={coursePay.overdue > 0}
                sub={coursePay.overdue > 0 ? t('sp_cp_overdue_sub', '{n} overdue — pay the course', { n: coursePay.overdue }) : t('sp_cp_dash_sub', 'Pay the course & mark done')}
                href="/portal/reseller/course-payments"
              />
            )}
            {(kpis.teeTimesToBook > 0 || kpis.teeTimesAutoBookedYtd > 0) && (
              <Tile
                label={t('sp_kpi_tobook_title', 'Tee times to book')}
                value={kpis.teeTimesToBook || 0}
                urgent={kpis.teeTimesToBookNext30 > 0}
                sub={[
                  `${kpis.teeTimesToBookNext30 || 0} ${t('sp_kpi_tobook_next30', 'within 30 days')}`,
                  (kpis.teeTimesAutoBookedYtd || kpis.teeTimesAutoBookedLast30)
                    ? `${t('sp_kpi_autobooked', 'Auto-booked')} ${kpis.teeTimesAutoBookedYtd || 0} / ${kpis.teeTimesAutoBookedLast30 || 0}`
                    : null,
                ].filter(Boolean).join(' · ')}
                href="/portal/reseller/to-book"
              />
            )}
          </div>
        </>
      )}

      <h2 style={h2}>{t('sp_dash_account_details', 'Account details')}</h2>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20 }}>
        <table style={{ width: '100%', fontSize: 14 }}>
          <tbody>
            <Row label={t('sp_name_label', 'Name')}      value={account?.Name} />
            <SlugRow slug={account?.Slug} onSaved={(s) => setMe(prev => ({ ...prev, account: { ...prev.account, Slug: s } }))} />
            <Row label={t('status', 'Status')}    value={account?.Status} />
            <Row label={t('sp_currency', 'Currency')}  value={account?.Currency} />
            <Row label={t('sp_dash_row_notification_mode', 'Notification mode')} value={account?.NotificationMode} />
            <Row label={t('sp_dash_row_custom_domain', 'Custom domain')} value={account?.CustomDomain || <span style={{ color: '#94a3b8' }}>{t('sp_dash_default_subdomain', 'default subdomain')}</span>} />
            <Row label={t('sp_dash_row_account_users', 'Account users')} value={`${me?.users?.length || 0}`} />
          </tbody>
        </table>
      </div>
    </ResellerShell>
  )
}

function Tile({ label, value, sub, urgent, href }) {
  const wrapperStyle = {
    background: urgent ? '#fef2f2' : '#fff',
    border: '1px solid ' + (urgent ? '#fecaca' : '#e2e8f0'),
    borderRadius: 8,
    padding: 18,
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
  }
  const inner = (
    <>
      <div style={{ fontSize: 11, color: urgent ? '#991b1b' : '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, marginTop: 6, color: urgent ? '#991b1b' : '#0f172a' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: urgent ? '#7f1d1d' : '#94a3b8', marginTop: 4 }}>{sub}</div>}
    </>
  )
  return href ? <Link to={href} style={wrapperStyle}>{inner}</Link> : <div style={wrapperStyle}>{inner}</div>
}

function ChecklistRow({ done, title, detail, cta, soon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 8 }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: done ? '#00d19f' : (soon ? '#e2e8f0' : '#fef3c7'),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: done ? '#fff' : '#475569', fontSize: 16, fontWeight: 700,
      }}>{done ? '✓' : (soon ? '·' : '!')}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{title}</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{detail}</div>
      </div>
      {cta && (
        <a href={cta.to} style={{ padding: '8px 16px', background: '#0f172a', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>{cta.label}</a>
      )}
    </div>
  )
}

function Row({ label, value }) {
  return (
    <tr>
      <td style={{ padding: '6px 0', color: '#64748b', width: 200 }}>{label}</td>
      <td>{value}</td>
    </tr>
  )
}

// Inline-editable account slug (used in the account-wide search embed URL).
// Server normalizes + enforces uniqueness across all reseller accounts.
function SlugRow({ slug, onSaved }) {
  const { t } = useLanguage()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(slug || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  async function save() {
    setSaving(true); setErr('')
    try {
      const { data } = await tournamentApi.patch('/portal/reseller/account', { slug: value })
      onSaved(data.slug)
      setEditing(false)
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
    } finally { setSaving(false) }
  }

  return (
    <tr>
      <td style={{ padding: '6px 0', color: '#64748b', width: 200, verticalAlign: 'top' }}>{t('sp_slug', 'Slug')}</td>
      <td style={{ padding: '6px 0' }}>
        {!editing ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <code>{slug}</code>
            <button type="button" onClick={() => { setValue(slug || ''); setErr(''); setEditing(true) }}
              style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', padding: 0 }}>
              {t('edit', 'Edit')}
            </button>
          </span>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input value={value} onChange={e => setValue(e.target.value)} autoFocus placeholder="my-storefront"
                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'monospace', width: 260 }} />
              <button type="button" onClick={save} disabled={saving}
                style={{ padding: '6px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? t('sp_saving', 'Saving…') : t('save', 'Save')}
              </button>
              <button type="button" onClick={() => { setEditing(false); setErr('') }}
                style={{ padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                {t('cancel', 'Cancel')}
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              {t('sp_slug_hint', 'Used in your search-embed URL. Letters, numbers and hyphens only; must be unique.')}
            </div>
            {err && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{err}</div>}
          </div>
        )}
      </td>
    </tr>
  )
}

const h2 = { margin: '24px 0 12px', fontSize: 16, color: '#0f172a' }

// ─── Booking Health card ────────────────────────────────────────────────
//
// Top-of-dashboard "are my customers' trips actually booked?" answer.
// Reads /portal/reseller/booking-health which aggregates the latest
// integrity-check rows. Green when all good, yellow/red when something
// needs human attention. Each issue has a one-click action.

function BookingHealthCard({ health, onResolve, onRecheck }) {
  const { t } = useLanguage()
  if (!health) return null
  const { status, upcomingCount, issues = [], lastCheckedAt, stats30d } = health

  // Single colored bar tells the whole story. Most days it's green and
  // the reseller scrolls past it.
  const healthLabel = t('sp_dash_booking_health', 'BOOKING HEALTH')
  const palette = {
    ok:       { bg: '#ecfdf5', border: '#86efac', accent: '#059669', icon: '🟢', label: healthLabel },
    warning:  { bg: '#fffbeb', border: '#fde68a', accent: '#92400e', icon: '🟡', label: healthLabel },
    critical: { bg: '#fef2f2', border: '#fca5a5', accent: '#991b1b', icon: '🔴', label: healthLabel },
    no_trips: { bg: '#f8fafc', border: '#e2e8f0', accent: '#64748b', icon: '⚪', label: healthLabel },
  }[status] || { bg: '#f8fafc', border: '#e2e8f0', accent: '#64748b', icon: '⚪', label: healthLabel }

  const headline =
    status === 'ok'        ? t('sp_dash_all_trips_confirmed', 'All {count} upcoming trip{plural} are confirmed.', { count: upcomingCount, plural: upcomingCount === 1 ? '' : 's' }) :
    status === 'warning'   ? t('sp_dash_trips_need_attention', '{count} trip{plural} attention.', { count: issues.length, plural: issues.length === 1 ? ' needs' : 's need' }) :
    status === 'critical'  ? t('sp_dash_trips_need_urgent', '{count} trip{plural} need urgent attention.', { count: issues.length, plural: issues.length === 1 ? '' : 's' }) :
    status === 'no_trips'  ? t('sp_dash_no_upcoming_trips', 'No upcoming trips yet.') :
                             '—'

  const lastCheckText = lastCheckedAt ? timeAgo(lastCheckedAt, t) : t('sp_dash_time_never', 'never')

  return (
    <div style={{
      background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 8,
      padding: '14px 16px', marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: status === 'ok' || status === 'no_trips' ? 0 : 10 }}>
        <span style={{ fontSize: 16 }}>{palette.icon}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: palette.accent, letterSpacing: 1 }}>{palette.label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>
          {t('sp_dash_last_checked', 'Last checked: {time}', { time: lastCheckText })}
          <button
            onClick={onRecheck}
            style={{ marginLeft: 10, padding: '2px 8px', fontSize: 11, color: palette.accent, background: 'transparent', border: `1px solid ${palette.border}`, borderRadius: 4, cursor: 'pointer' }}
            title={t('sp_dash_recheck_tooltip', 'Force a fresh upstream check')}
          >
            {t('sp_dash_recheck_now', 'Recheck now')}
          </button>
        </span>
      </div>
      <div style={{ fontSize: 14, color: '#0f172a', fontWeight: status === 'ok' ? 500 : 600 }}>
        {headline} {status === 'ok' && '✓'}
      </div>

      {issues.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {issues.map(iss => (
            <IssueRow key={`${iss.bookingId}-${iss.issueType}`} issue={iss} onResolve={onResolve} />
          ))}
        </div>
      )}

      {stats30d && stats30d.checked > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${palette.border}`, fontSize: 11, color: '#64748b' }}>
          {t('sp_dash_last_30_days_checks', 'Last 30 days: {count} check{plural} run', { count: stats30d.checked, plural: stats30d.checked === 1 ? '' : 's' })}
          {stats30d.discrepancies > 0 ? t('sp_dash_issues_flagged', ', {count} issue{plural} flagged', { count: stats30d.discrepancies, plural: stats30d.discrepancies === 1 ? '' : 's' }) : t('sp_dash_no_issues_found', ', no issues found')}.
        </div>
      )}
    </div>
  )
}

function IssueRow({ issue, onResolve }) {
  const { t } = useLanguage()
  const sev = {
    critical: { icon: '⚠', accent: '#991b1b', bg: '#fff' },
    warning:  { icon: '⚠', accent: '#92400e', bg: '#fff' },
    info:     { icon: 'ℹ', accent: '#0369a1', bg: '#fff' },
  }[issue.severity] || { icon: '⚠', accent: '#92400e', bg: '#fff' }
  const dateStr = new Date(issue.checkInDate).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
  const inLabel = issue.daysUntil === 0 ? t('sp_dash_issue_today', 'today') : issue.daysUntil === 1 ? t('sp_dash_issue_tomorrow', 'tomorrow') : t('sp_dash_issue_in_days', 'in {n} days', { n: issue.daysUntil })

  return (
    <div style={{ background: sev.bg, border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ color: sev.accent, fontSize: 14, fontWeight: 700 }}>{sev.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>
            {issue.customerName} <span style={{ color: '#64748b', fontWeight: 400 }}>({dateStr}, {inLabel})</span>
          </div>
          <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>{issue.headline}</div>
          {issue.suggestedAction && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>→ {issue.suggestedAction}</div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
          <Link to={issue.actionUrl}
            style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, color: '#fff', background: '#0f172a', borderRadius: 4, textDecoration: 'none', textAlign: 'center' }}>
            {t('sp_dash_view_booking', 'View booking')}
          </Link>
        </div>
      </div>
    </div>
  )
}

function timeAgo(when, t) {
  const ms = Date.now() - new Date(when).getTime()
  if (ms < 60_000)         return t('sp_dash_time_just_now', 'just now')
  if (ms < 3600_000)       return t('sp_dash_time_min_ago', '{n} min ago', { n: Math.round(ms / 60_000) })
  if (ms < 86_400_000)     return t('sp_dash_time_hours_ago', '{n}h ago', { n: Math.round(ms / 3_600_000) })
  return t('sp_dash_time_days_ago', '{n}d ago', { n: Math.round(ms / 86_400_000) })
}
